import { Center, Group, Loader, Pagination, Table, Text } from "@mantine/core";
import { format } from "date-fns";
import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { Tag } from "@/models/tag";
import { TagAssignmentRun } from "@/models/tagAssignmentRun";
import { TagAssignmentStatus } from "@/services/tagAssignment/tagAssignment.api.types";

interface Props {
  runs: TagAssignmentRun[] | undefined;
  isLoading: boolean;
  tags: Tag[] | undefined;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  offset: number;
}

const translateStatus = (status: TagAssignmentStatus, t: TFunction) =>
  match(status)
    .with("QUEUED", () => t("tagAssignment.status.queued"))
    .with("RUNNING", () => t("tagAssignment.status.running"))
    .with("COMPLETED", () => t("tagAssignment.status.completed"))
    .with("FAILED", () => t("tagAssignment.status.failed"))
    .exhaustive();

export const TagAssignmentHistoryTable: React.FC<Props> = ({
  runs,
  isLoading,
  tags,
  currentPage,
  totalPages,
  onPageChange,
  offset,
}) => {
  const { t } = useTranslation("admin");

  const getTagNames = (tagIds: string[]): string => {
    if (!tags || tagIds.length === 0) return "-";
    const tagNames = tagIds
      .map((id) => tags.find((tag) => tag.id === id)?.name)
      .filter((name): name is string => name !== undefined);
    return tagNames.length > 0 ? tagNames.join(", ") : "-";
  };

  if (isLoading) {
    return (
      <Center h={100}>
        <Loader color="gray" size="sm" data-testid="loader" />
      </Center>
    );
  }

  return (
    <>
      <Table data-testid="tag-assignment-history-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>#</Table.Th>
            <Table.Th>{t("tagAssignment.history.targetPeriod")}</Table.Th>
            <Table.Th>{t("tagAssignment.history.createdAt")}</Table.Th>
            <Table.Th>{t("tagAssignment.history.updatedAt")}</Table.Th>
            <Table.Th>{t("tagAssignment.history.status")}</Table.Th>
            <Table.Th>{t("tagAssignment.history.tags")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {runs && runs.length > 0
            ? runs.map((run, index) => (
              <Table.Tr key={run.id}>
                <Table.Td>{offset + index + 1}</Table.Td>
                <Table.Td>
                  {format(run.startDate, "yyyy-MM-dd") + " 〜 " + format(run.endDate, "yyyy-MM-dd")}
                </Table.Td>
                <Table.Td>{format(run.createdAt, "yyyy-MM-dd HH:mm:ss")}</Table.Td>
                <Table.Td>{format(run.updatedAt, "yyyy-MM-dd HH:mm:ss")}</Table.Td>
                <Table.Td>{translateStatus(run.status, t)}</Table.Td>
                <Table.Td maw="30vw">
                  <Text size="sm" lineClamp={2}>
                    {getTagNames(run.tagIds)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))
            : (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed">{t("tagAssignment.history.noHistory")}</Text>
                </Table.Td>
              </Table.Tr>
            )}
        </Table.Tbody>
      </Table>
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination value={currentPage} onChange={onPageChange} total={totalPages} />
        </Group>
      )}
    </>
  );
};
